/**
 * HeadlinesTable — presentational wrapper around the shared <DataTable />.
 *
 * Filters are lifted to the page (search, team multi-select, owner multi-select)
 * because search + team drive the server-side fetch. We adapt those props into
 * the `columnFilters` shape DataTable expects.
 */

import { useCallback, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Archive, ArchiveRestore } from 'lucide-react';
import { DataTable } from '@/shared/components/data-table';
import type { DataTableColumn } from '@/shared/components/data-table';
import { OwnerAvatar } from '@/shared/components/OwnerAvatar';
import { useUserTeamIds, useIsLeadership } from '@/hooks/useSession';
import { useArchiveHeadline, useUnarchiveHeadline } from '../hooks/useHeadlines';
import type { Headline, HeadlineOwner } from '../types';
import type { Team } from '@/features/rocks/types';

interface HeadlinesTableProps {
  headlines: Headline[];
  count: number;
  emptyMessage: string;
  canManage: boolean;
  teams?: Team[];
  onSelectHeadline?: (headline: Headline) => void;
  onCreateHeadline?: () => void;
  selectedHeadlineId?: string | null;
  searchInput?: string;
  onSearchChange?: (value: string) => void;
  selectedTeamIds?: string[];
  onSelectedTeamIdsChange?: (teamIds: string[]) => void;
  owners?: HeadlineOwner[];
  selectedOwnerIds?: string[];
  onSelectedOwnerIdsChange?: (ids: string[]) => void;
}

interface Ctx {
  teams: Team[];
  canManage: boolean;
  selectedHeadlineId?: string | null;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return date.toLocaleDateString('en-CA');
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-CA');
}

export function HeadlinesTable({
  headlines,
  count: _count,
  emptyMessage,
  canManage,
  teams = [],
  onSelectHeadline,
  selectedHeadlineId,
  searchInput = '',
  onSearchChange,
  selectedTeamIds = [],
  onSelectedTeamIdsChange,
  owners = [],
  selectedOwnerIds = [],
  onSelectedOwnerIdsChange,
}: HeadlinesTableProps) {
  const archiveMutation = useArchiveHeadline();
  const unarchiveMutation = useUnarchiveHeadline();
  const userTeamIds = useUserTeamIds();
  const isLeadership = useIsLeadership();
  const canManageHeadline = useCallback((headline: Headline) => {
    if (!canManage) return false;
    if (isLeadership) return true;
    const itemTeams = headline.team_ids ?? [];
    if (itemTeams.length === 0) return false;
    return itemTeams.some((tid) => userTeamIds.includes(tid));
  }, [canManage, isLeadership, userTeamIds]);

  // Column sort is client-side
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);

  const handleSort = useCallback((key: string) => {
    if (sortKey === key) setSortDirection((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(key);
      setSortDirection(1);
    }
  }, [sortKey]);

  // Adapt controlled filter props to the DataTable columnFilters shape.
  const columnFilters = useMemo<Record<string, string>>(() => ({
    title: searchInput,
    team: selectedTeamIds.length ? JSON.stringify(selectedTeamIds) : '',
    owner: selectedOwnerIds.length ? JSON.stringify(selectedOwnerIds) : '',
  }), [searchInput, selectedTeamIds, selectedOwnerIds]);

  const setColumnFilter = useCallback((key: string, value: string) => {
    if (key === 'title') {
      onSearchChange?.(value);
    } else if (key === 'team') {
      try { onSelectedTeamIdsChange?.(value ? JSON.parse(value) : []); } catch { /* ignore */ }
    } else if (key === 'owner') {
      try { onSelectedOwnerIdsChange?.(value ? JSON.parse(value) : []); } catch { /* ignore */ }
    }
  }, [onSearchChange, onSelectedTeamIdsChange, onSelectedOwnerIdsChange]);

  const teamOptions = useMemo(
    () => teams.map((t) => ({ label: t.name, value: t.id })),
    [teams],
  );
  const ownerOptions = useMemo(
    () => owners.map((o) => ({ label: o.name, value: o.id })),
    [owners],
  );

  const columns = useMemo<DataTableColumn<Headline, Ctx>[]>(() => {
    const cols: DataTableColumn<Headline, Ctx>[] = [
      {
        key: 'title',
        label: 'Title',
        sortKey: 'title',
        filterType: 'text',
        isPrimarySearch: true,
        searchPlaceholder: 'Search title or details...',
        getValue: (h) => h.title,
        render: (h) => (
          <div>
            <p className="font-medium text-sm">{h.title}</p>
            {h.body && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{h.body}</p>
            )}
            {h.is_archived && (
              <Badge variant="outline" className="mt-1 text-xs">Archived</Badge>
            )}
          </div>
        ),
      },
      {
        key: 'created_at',
        label: 'Created',
        sortKey: 'created_at',
        noFilter: true,
        getValue: (h) => h.created_at ?? '',
        render: (h) => (
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {formatRelativeTime(h.created_at)}
          </span>
        ),
      },
      {
        key: 'scheduled_at',
        label: 'Publish date',
        sortKey: 'scheduled_at',
        noFilter: true,
        // Sort by scheduled_at if set, otherwise by created_at — keeps the
        // "effective publish date" ordering coherent for immediate publishes.
        getValue: (h) => h.scheduled_at ?? h.created_at ?? '',
        render: (h) => {
          const iso = h.scheduled_at ?? h.created_at;
          if (!iso) return <span className="text-muted-foreground">—</span>;
          const d = new Date(iso);
          if (Number.isNaN(d.getTime())) return <span className="text-muted-foreground">—</span>;
          // yyyy-mm-dd (local tz) — matches the form's date input format.
          const pad = (n: number) => String(n).padStart(2, '0');
          const label = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
          const isFuture = h.scheduled_at ? d.getTime() > Date.now() : false;
          return (
            <span className={`text-sm whitespace-nowrap ${isFuture ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
              {label}
              {isFuture && <span className="ml-1 text-[10px] uppercase tracking-wide">(upcoming)</span>}
            </span>
          );
        },
      },
      {
        key: 'team',
        label: 'Team',
        sortKey: 'team',
        filterType: 'select',
        filterOptions: teamOptions,
        filterAllLabel: 'All Teams',
        getValue: (h, ctx) => {
          const names = (h.team_ids ?? [])
            .map((id) => ctx.teams.find((t) => t.id === id)?.name)
            .filter((n): n is string => !!n);
          return names.join(', ');
        },
        render: (h, ctx) => {
          const names = (h.team_ids ?? [])
            .map((id) => ctx.teams.find((t) => t.id === id)?.name)
            .filter((n): n is string => !!n);
          if (names.length === 0) return <span className="text-muted-foreground">—</span>;
          const shown = names.length <= 2 ? names.join(', ') : `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
          return <span className="text-sm text-muted-foreground whitespace-nowrap">{shown}</span>;
        },
      },
      {
        key: 'owner',
        label: 'Owner',
        sortKey: 'owner',
        filterType: 'select',
        filterOptions: ownerOptions,
        filterAllLabel: 'All Owners',
        getValue: (h) => h.owner_name ?? '',
        render: (h) => {
          if (!h.owner_name) return <span className="text-sm text-muted-foreground">—</span>;
          return (
            <div className="flex items-center gap-2">
              <OwnerAvatar ownerId={h.owner_id} name={h.owner_name} />
              <span className="text-sm text-muted-foreground">{h.owner_name}</span>
            </div>
          );
        },
      },
    ];

    if (canManage) {
      cols.push({
        key: 'actions',
        label: '',
        noFilter: true,
        className: 'w-[50px]',
        getValue: () => '',
        render: (h, ctx) => {
          if (!canManageHeadline(h)) return null;
          return (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              title={h.is_archived ? 'Restore' : 'Archive'}
              aria-label={h.is_archived ? 'Restore headline' : 'Archive headline'}
              onClick={(e) => {
                e.stopPropagation();
                if (h.is_archived) ctx.onUnarchive(h.id);
                else ctx.onArchive(h.id);
              }}
            >
              {h.is_archived ? (
                <ArchiveRestore className="h-4 w-4" />
              ) : (
                <Archive className="h-4 w-4" />
              )}
            </Button>
          );
        },
      });
    }

    return cols;
  }, [canManage, canManageHeadline, teamOptions, ownerOptions]);

  const ctx: Ctx = useMemo(() => ({
    teams,
    canManage,
    selectedHeadlineId,
    onArchive: (id: string) => archiveMutation.mutate(id),
    onUnarchive: (id: string) => unarchiveMutation.mutate(id),
  }), [teams, canManage, selectedHeadlineId, archiveMutation, unarchiveMutation]);

  // Client-side sort — parent filters via server query so headlines is already filtered.
  const sortedHeadlines = useMemo(() => {
    const col = columns.find((c) => c.sortKey === sortKey);
    if (!col) return headlines;
    return [...headlines].sort((a, b) => {
      const av = col.getValue(a, ctx);
      const bv = col.getValue(b, ctx);
      return av.localeCompare(bv, undefined, { sensitivity: 'base' }) * sortDirection;
    });
  }, [headlines, columns, ctx, sortKey, sortDirection]);

  return (
    <DataTable<Headline, Ctx>
      items={sortedHeadlines}
      columns={columns}
      ctx={ctx}
      columnFilters={columnFilters}
      sortKey={sortKey}
      sortDirection={sortDirection}
      onSort={handleSort}
      onSetColumnFilter={setColumnFilter}
      onRowClick={onSelectHeadline}
      emptyState={emptyMessage}
    />
  );
}
